/**
 * S4 (AUDIT-INCIDENT-HISTORY PRD v1) — PDF export of the S3 review payload.
 *
 * Follows client/src/utils/pdfExport.ts conventions: a PURE section builder
 * (unit-testable in jsdom — deterministic headings/lines, no jsPDF imports)
 * + a browser-only renderer that dynamically imports jsPDF (so jsdom tests
 * skip it). Letter format, 0.75in margins, per-page footer with page number.
 *
 * Content (PRD §4 binding — every number comes straight from the API payload;
 * empty periods print ZEROS, never blanks or invented values):
 *   period header (from/to + comparison window) → entity counts → findings
 *   trends → incident trends → top risks (topCategories with share %) → open
 *   corrective actions (aging buckets) → leading indicators.
 * The annual variant (GET /api/reports/review/annual) shares the section
 * builders; the period header shows the year and Q1..Q4 buckets print.
 */
import type { ReviewPayload, AnnualReviewPayload, CountStat, Stat } from './reviewPayloadTypes';

export type ReviewPdfSectionKind = 'period' | 'counts' | 'trends' | 'aging' | 'indicators';

export interface ReviewPdfSection {
  kind: ReviewPdfSectionKind;
  heading: string;
  lines: string[];
}

export interface ReviewPdfInput {
  payload: ReviewPayload;
}

export interface AnnualPdfInput {
  annual: AnnualReviewPayload;
}

const fmtDate = (iso: string) => iso.slice(0, 10);
const deltaStr = (delta: number) => `Δ ${delta > 0 ? '+' : ''}${delta}`;
const statLine = (label: string, s: Stat): string => `${label}: ${s.count} (previous ${s.previous}, ${deltaStr(s.delta)})`;
const countLine = (c: CountStat): string => `${c.key}: ${c.count} (previous ${c.previous}, ${deltaStr(c.delta)})`;

function periodLines(period: ReviewPayload['period']): string[] {
  return [
    `Period: ${fmtDate(period.from)} to ${fmtDate(period.to)}`,
    `Comparison window: ${fmtDate(period.previousFrom)} to ${fmtDate(period.previousTo)} (same-length prior period)`,
  ];
}

function findingsSections(p: ReviewPayload): ReviewPdfSection[] {
  const f = p.findings;
  const sections: ReviewPdfSection[] = [];
  const trendLines: string[] = [
    statLine('Findings', f.total),
    'By severity:',
    ...f.bySeverity.map(countLine),
    'By status:',
    ...f.byStatus.map(countLine),
    'By finding type:',
    ...f.byFindingType.map(countLine),
    'By element:',
    ...f.byElementName.map(countLine),
  ];
  sections.push({ kind: 'trends', heading: 'FINDINGS TRENDS', lines: trendLines });
  sections.push({
    kind: 'aging',
    heading: 'OPEN CORRECTIVE ACTIONS',
    lines: [
      statLine('Overdue', f.openActionAging.overdue),
      `Not yet due: ${f.openActionAging.buckets.notDue.count} (previous ${f.openActionAging.buckets.notDue.previous}, ${deltaStr(f.openActionAging.buckets.notDue.delta)})`,
      `1–30 days overdue: ${f.openActionAging.buckets.days1to30.count} (previous ${f.openActionAging.buckets.days1to30.previous}, ${deltaStr(f.openActionAging.buckets.days1to30.delta)})`,
      `31–90 days overdue: ${f.openActionAging.buckets.days31to90.count} (previous ${f.openActionAging.buckets.days31to90.previous}, ${deltaStr(f.openActionAging.buckets.days31to90.delta)})`,
      `90+ days overdue: ${f.openActionAging.buckets.days90plus.count} (previous ${f.openActionAging.buckets.days90plus.previous}, ${deltaStr(f.openActionAging.buckets.days90plus.delta)})`,
    ],
  });
  return sections;
}

function incidentSections(p: ReviewPayload): ReviewPdfSection[] {
  const i = p.incidents;
  // MED-4: form-data facets print with the same per-key honesty as the page;
  // an absent/valueless facet prints the empty-state line instead of silence.
  const facetLines: string[] = [];
  if (i.facets?.length) {
    i.facets.forEach((facet) => {
      const label = facet.path ? facet.path.split('.').pop()! : '(unknown path)';
      facetLines.push(`Facet ${label} (${facet.path || 'unknown path'}):`);
      facetLines.push(...(facet.values.length ? facet.values.map((v) => `  ${countLine(v)}`) : ['  No facet values recorded for this period.']));
    });
  } else {
    facetLines.push('No facet values recorded for this period.');
  }
  return [
    {
      kind: 'trends',
      heading: 'INCIDENT TRENDS',
      lines: [
        statLine('Incidents', i.total),
        statLine('Near misses', i.nearMissCount),
        'By type:',
        ...i.byType.map(countLine),
        'By severity:',
        ...i.bySeverity.map(countLine),
        ...facetLines,
      ],
    },
  ];
}

function topRisksSection(p: ReviewPayload): ReviewPdfSection {
  return {
    kind: 'trends',
    heading: 'TOP RISKS (TOP CATEGORIES BY SHARE)',
    lines: p.findings.topCategories.map(
      (c) => `${c.key}: ${c.count} (previous ${c.previous}, ${deltaStr(c.delta)}, share ${c.sharePct}%)`,
    ),
  };
}

function leadingIndicatorsSection(p: ReviewPayload): ReviewPdfSection {
  const li = p.leadingIndicators;
  const group = (label: string, rows: CountStat[]) => [`${label}:`, ...rows.map((c) => `  ${countLine(c)}`)];
  return {
    kind: 'indicators',
    heading: 'LEADING INDICATORS',
    lines: [
      ...group('Investigation factors by 5M category', li.investigationFactorsByCategory),
      ...group('Audits by contractor', li.auditsByContractor),
      ...group('Audits by site', li.auditsBySite),
      ...group('Audits by rig type', li.auditsByRigType),
      ...group('Audits by supervisor', li.auditsBySupervisor),
    ],
  };
}

/** Core section list for ONE review payload (shared by period + annual). */
export function reviewSectionsForPayload(p: ReviewPayload, periodLinesOverride?: string[]): ReviewPdfSection[] {
  return [
    { kind: 'period', heading: 'SAFETY REVIEW', lines: periodLinesOverride ?? periodLines(p.period) },
    {
      kind: 'counts',
      heading: 'ENTITY COUNTS',
      lines: [statLine('Findings', p.findings.total), statLine('Incidents', p.incidents.total)],
    },
    ...findingsSections(p),
    ...incidentSections(p),
    topRisksSection(p),
    leadingIndicatorsSection(p),
  ];
}

/**
 * Period review PDF sections. PURE — unit-tested in jsdom.
 */
export function buildReviewPdfSections(input: ReviewPdfInput): ReviewPdfSection[] {
  return reviewSectionsForPayload(input.payload);
}

/**
 * Annual variant: period header shows the year; Q1..Q4 buckets each print
 * their headline counts, then the full-year totals. PURE.
 */
export function buildAnnualPdfSections(input: AnnualPdfInput): ReviewPdfSection[] {
  const { annual } = input;
  const sections: ReviewPdfSection[] = [
    {
      kind: 'period',
      heading: 'ANNUAL SAFETY REVIEW',
      lines: [`Year: ${annual.year}`, 'Aggregated by calendar quarter, each compared to the prior year same quarter.'],
    },
  ];
  annual.quarters.forEach((q) => {
    sections.push({
      kind: 'counts',
      heading: `Q${q.quarter}`,
      lines: [
        statLine('Findings', q.findings.total),
        statLine('Incidents', q.incidents.total),
        statLine('Overdue corrective actions', q.findings.openActionAging.overdue),
        statLine('Near misses', q.incidents.nearMissCount),
      ],
    });
  });
  sections.push({
    kind: 'counts',
    heading: 'FULL YEAR TOTALS',
    lines: [
      statLine('Findings', annual.totals.findings.total),
      statLine('Incidents', annual.totals.incidents.total),
      statLine('Overdue corrective actions', annual.totals.findings.openActionAging.overdue),
      statLine('Near misses', annual.totals.incidents.nearMissCount),
      'Findings by severity:',
      ...annual.totals.findings.bySeverity.map(countLine),
      'Incidents by type:',
      ...annual.totals.incidents.byType.map(countLine),
      ...annual.totals.findings.topCategories.map(
        (c) => `Top risk ${c.key}: ${c.count} (share ${c.sharePct}%)`,
      ),
    ],
  });
  return sections;
}

/** File name for the period review PDF (repo sanitization pattern). */
export function reviewPdfFileName(fromIso: string, toIso: string): string {
  return 'safety-review_' + fromIso.slice(0, 10) + '_' + toIso.slice(0, 10) + '.pdf';
}

// ---------------------------------------------------------------------------
// jsPDF rendering (browser-only; dynamically imported so jsdom tests skip it)
// ---------------------------------------------------------------------------
const PAGE_W = 8.5;
const PAGE_H = 11;
const MARGIN = 0.75;

export async function exportReviewPdf(
  input: ReviewPdfInput,
  fileName?: string,
): Promise<void> {
  const sections = buildReviewPdfSections(input);
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'in', format: 'letter' });

  let y = MARGIN;
  const ensureSpace = (needed: number) => {
    if (y + needed > PAGE_H - MARGIN - 0.3) {
      footer(doc);
      doc.addPage();
      y = MARGIN;
    }
  };
  sections.forEach((section) => {
    ensureSpace(0.5);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(section.heading, MARGIN, y);
    y += 0.08;
    doc.setDrawColor(180);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 0.14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    section.lines.forEach((line) => {
      const wrapped = doc.splitTextToSize(line || ' ', PAGE_W - 2 * MARGIN) as string[];
      wrapped.forEach((seg) => {
        ensureSpace(0.18);
        doc.text(seg, MARGIN + 0.15, y);
        y += 0.16;
      });
    });
    y += 0.14;
  });
  footer(doc);
  const name = fileName ?? reviewPdfFileName(input.payload.period.from, input.payload.period.to);
  doc.save(name);
}

export async function exportAnnualReviewPdf(
  input: AnnualPdfInput,
  fileName?: string,
): Promise<void> {
  const sections = buildAnnualPdfSections(input);
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'in', format: 'letter' });
  let y = MARGIN;
  const ensureSpace = (needed: number) => {
    if (y + needed > PAGE_H - MARGIN - 0.3) {
      footer(doc);
      doc.addPage();
      y = MARGIN;
    }
  };
  sections.forEach((section) => {
    ensureSpace(0.5);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(section.heading, MARGIN, y);
    y += 0.08;
    doc.setDrawColor(180);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 0.14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    section.lines.forEach((line) => {
      const wrapped = doc.splitTextToSize(line || ' ', PAGE_W - 2 * MARGIN) as string[];
      wrapped.forEach((seg) => {
        ensureSpace(0.18);
        doc.text(seg, MARGIN + 0.15, y);
        y += 0.16;
      });
    });
    y += 0.14;
  });
  footer(doc);
  doc.save(fileName ?? `safety-review_${input.annual.year}.pdf`);
}

function footer(doc: import('jspdf').jsPDF): void {
  doc.setFontSize(8);
  doc.setTextColor(90);
  doc.text('Rigging-Pro — company-scoped safety review (recomputed on request)', MARGIN, PAGE_H - 0.4);
  doc.text(`Page ${doc.getNumberOfPages()}`, PAGE_W - MARGIN, PAGE_H - 0.4, { align: 'right' });
  doc.setTextColor(0);
}
